import { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../utils/api';

const sug = {
  drop: {
    position:'absolute', top:'100%', left:0, right:0,
    background:'#fff', borderRadius:12, zIndex:50,
    boxShadow:'0 8px 30px rgba(0,0,0,0.12)',
    overflow:'hidden', marginTop:4,
  },
  item: {
    padding:'12px 14px', fontSize:14, color:'#1D1D1F',
    cursor:'pointer', borderBottom:'1px solid #F5F5F7',
    background:'#fff',
  },
};

export default function RecurringPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Payment Links';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const [links, setLinks]       = useState([]);
  const [amount, setAmount]     = useState('');
  const [desc, setDesc]         = useState('');
  const [webhook, setWebhook]   = useState('');
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg]           = useState('');
  const [expanded, setExpanded] = useState(null);
  const [qrLink, setQrLink]     = useState(null);
  const [descSuggestions, setDescSuggestions]       = useState([]);
  const [webhookSuggestions, setWebhookSuggestions] = useState([]);
  const [showDescDrop, setShowDescDrop]             = useState(false);
  const [showWebhookDrop, setShowWebhookDrop]       = useState(false);

  useEffect(() => {
    setDescSuggestions(JSON.parse(localStorage.getItem('tb_rec_descriptions') || '[]'));
    setWebhookSuggestions(JSON.parse(localStorage.getItem('tb_webhooks') || '[]'));
  }, []);

  function saveToHistory(key, value) {
    if (!value || !value.trim()) return;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const updated = [value, ...existing.filter(v => v !== value)];
    localStorage.setItem(key, JSON.stringify(updated));
  }
  const qrRef                   = useRef(null);

  useEffect(() => { loadLinks(); }, []);

  async function loadLinks() {
    try {
      const res = await api.get('/payments/recurring');
      setLinks((res.data || []).filter(l => l.active !== false));
    } catch (err) { console.error(err); }
  }

  async function createLink() {
    if (!amount || parseFloat(amount) < 0.01) return;
    setCreating(true);
    try {
      const res = await api.post('/payments/recurring', {
        amountCents: Math.round(parseFloat(amount) * 100),
        description: desc,
        webhookUrl:  webhook || null,
      });
      saveToHistory('tb_rec_descriptions', desc);
      saveToHistory('tb_webhooks', webhook);
      setDescSuggestions(JSON.parse(localStorage.getItem('tb_rec_descriptions') || '[]'));
      setWebhookSuggestions(JSON.parse(localStorage.getItem('tb_webhooks') || '[]'));
      setLinks([res.data, ...links]);
      setAmount(''); setDesc(''); setWebhook('');
      setShowForm(false);
      setMsg('Payment link created!');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) { setMsg('Failed to create'); }
    setCreating(false);
  }

  async function deleteLink(id) {
    if (!window.confirm('Delete this payment link?')) return;
    try {
      await api.delete('/payments/recurring/' + id);
      setLinks(links.filter(l => l.id !== id));
      if (expanded === id) setExpanded(null);
      if (qrLink?.id === id) setQrLink(null);
    } catch (err) { console.error(err); }
  }

  function copyLink(text) {
    navigator.clipboard.writeText(text);
    setMsg('Copied!');
    setTimeout(() => setMsg(''), 2000);
  }

  function downloadQR(link) {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 400; canvas.height = 400;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);
      const a = document.createElement('a');
      a.download = `thunderbolt-${link.shortCode || link.id.slice(0,8)}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }

  return (
    <div style={s.page}>

      {/* Header */}
      <div style={s.header}>
        <div style={s.title}>Payment Links</div>
        <button style={s.newBtn} onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New Link'}
        </button>
      </div>

      {/* Success message */}
      {msg && (
        <div style={s.toast}>{msg}</div>
      )}

      {/* Create form */}
      {showForm && (
        <div style={s.section}>
          <div style={s.card}>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>Amount (USD)</label>
              <div style={s.amountRow}>
                <span style={s.dollar}>$</span>
                <input
                  style={s.amountInput}
                  type="number" placeholder="0.00"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                />
              </div>
            </div>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>Description</label>
              <div style={{position:'relative'}}>
                <input
                  style={s.input}
                  placeholder="Monthly subscription, etc."
                  value={desc}
                  onChange={e => { setDesc(e.target.value); setShowDescDrop(true); }}
                  onFocus={() => setShowDescDrop(true)}
                  onBlur={() => setTimeout(() => setShowDescDrop(false), 150)}
                />
                {showDescDrop && desc.length > 0 && descSuggestions.filter(s => s.toLowerCase().includes(desc.toLowerCase())).slice(0,5).length > 0 && (
                  <div style={sug.drop}>
                    {descSuggestions.filter(s => s.toLowerCase().includes(desc.toLowerCase())).slice(0,5).map((s, i) => (
                      <div key={i} style={sug.item} onMouseDown={() => { setDesc(s); setShowDescDrop(false); }}>{s}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>Webhook URL (optional)</label>
              <div style={{position:'relative'}}>
                <input
                  style={s.input}
                  placeholder="https://yoursite.com/webhook"
                  value={webhook}
                  onChange={e => { setWebhook(e.target.value); setShowWebhookDrop(true); }}
                  onFocus={() => setShowWebhookDrop(true)}
                  onBlur={() => setTimeout(() => setShowWebhookDrop(false), 150)}
                />
                {showWebhookDrop && webhook.length > 0 && webhookSuggestions.filter(s => s.toLowerCase().includes(webhook.toLowerCase())).slice(0,5).length > 0 && (
                  <div style={sug.drop}>
                    {webhookSuggestions.filter(s => s.toLowerCase().includes(webhook.toLowerCase())).slice(0,5).map((s, i) => (
                      <div key={i} style={sug.item} onMouseDown={() => { setWebhook(s); setShowWebhookDrop(false); }}>{s}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button style={s.saveBtn} onClick={createLink} disabled={creating}>
              {creating ? 'Creating…' : 'Create Payment Link'}
            </button>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {qrLink && (
        <div style={s.modalOverlay} onClick={() => setQrLink(null)}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>${qrLink.amountUsd}</div>
            {qrLink.description && <div style={s.modalDesc}>{qrLink.description}</div>}
            <div style={s.qrWrap} ref={qrRef}>
              <QRCodeSVG value={qrLink.payUrl} size={200} level="H" includeMargin={true}/>
            </div>
            <div style={s.modalUrl}>{qrLink.payUrl}</div>
            <div style={s.modalBtns}>
              <button style={s.modalBtn} onClick={() => copyLink(qrLink.payUrl)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                Copy Link
              </button>
              <button style={{...s.modalBtn, background:'#007AFF', color:'#fff'}} onClick={() => downloadQR(qrLink)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                Download QR
              </button>
            </div>
            <button style={s.modalClose} onClick={() => setQrLink(null)}>Done</button>
          </div>
        </div>
      )}

      {/* Links list */}
      <div style={s.section}>
        <div style={s.sectionHeader}>
          <span style={s.sectionTitle}>Your Links</span>
          <span style={s.sectionCount}>{links.length} links</span>
        </div>

        {links.length === 0 && !showForm ? (
          <div style={s.empty}>
            <div style={s.emptyIcon}>🔗</div>
            <div style={s.emptyText}>No recurring links yet</div>
            <div style={s.emptySub}>Create one to accept repeated payments</div>
          </div>
        ) : (
          <div style={s.txList}>
            {links.map(link => (
              <div key={link.id}>
                <div style={s.txItem} onClick={() => setExpanded(expanded === link.id ? null : link.id)}>
                  <div style={{...s.txDot, background:'#EBF4FF', color:'#007AFF'}}>↻</div>
                  <div style={s.txInfo}>
                    <div style={s.txLabel}>{link.description || 'Recurring Payment'}</div>
                    <div style={s.txDate}>{link.payUrl}</div>
                  </div>
                  <div style={{...s.txAmount, color:'#007AFF'}}>${link.amountUsd}</div>
                </div>

                {expanded === link.id && (
                  <div style={s.detail}>
                    <div style={s.detailRow}>
                      <span style={s.detailLabel}>Amount</span>
                      <span style={s.detailValue}>${link.amountUsd}</span>
                    </div>
                    <div style={s.detailRow}>
                      <span style={s.detailLabel}>Description</span>
                      <span style={s.detailValue}>{link.description || '—'}</span>
                    </div>
                    <div style={s.detailRow}>
                      <span style={s.detailLabel}>Payment Link</span>
                      <div style={s.linkActions}>
                        <span style={s.linkUrl}>{link.payUrl}</span>
                        <button style={s.copyBtn} onClick={e => { e.stopPropagation(); copyLink(link.payUrl); }}>
                          Copy
                        </button>
                      </div>
                    </div>
                    <div style={s.detailRow}>
                      <span style={s.detailLabel}>ID</span>
                      <span style={{...s.detailValue, fontFamily:'monospace', fontSize:11, cursor:'pointer'}}
                        onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(link.id); }}>
                        {link.id} <span style={{color:'#007AFF', fontSize:10}}>⧉</span>
                      </span>
                    </div>

                    {/* Action buttons */}
                    <div style={s.actionRow}>
                      <button style={s.qrBtn} onClick={e => { e.stopPropagation(); setQrLink(link); }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3M17 14h3M14 17v3"/></svg>
                        QR Code
                      </button>
                      <button style={s.qrBtn} onClick={e => {
                        e.stopPropagation();
                        if (navigator.share) {
                          navigator.share({ title: 'Pay me via Thunderbolt', text: 'Pay $' + link.amountUsd + (link.description ? ' for ' + link.description : ''), url: link.payUrl });
                        } else {
                          copyLink(link.payUrl);
                        }
                      }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/></svg>
                        Share
                      </button>
                      <button style={s.deleteBtn} onClick={e => { e.stopPropagation(); deleteLink(link.id); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page:         { minHeight: '100vh', background: '#F5F5F7', paddingBottom: 88 },
  header:       { padding: '56px 20px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' },
  title:        { fontSize: 28, fontWeight: 700, letterSpacing: -0.5, color: '#1D1D1F' },
  newBtn:       { background: '#1D1D1F', color: '#fff', border: 'none', borderRadius: 20, padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  toast:        { margin: '0 16px 12px', background: '#E6FAF5', borderRadius: 12, padding: '12px 16px', fontSize: 14, color: '#00C48C', fontWeight: 500, textAlign: 'center' },

  section:      { margin: '0 16px 16px' },
  sectionHeader:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionTitle: { fontSize: 15, fontWeight: 600, color: '#1D1D1F' },
  sectionCount: { fontSize: 12, color: '#AEAEB2' },

  card:         { background: '#fff', borderRadius: 20, padding: 20, marginBottom: 12, boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  fieldGroup:   { marginBottom: 16 },
  fieldLabel:   { fontSize: 12, fontWeight: 500, color: '#6E6E73', marginBottom: 6, display: 'block' },
  amountRow:    { display: 'flex', alignItems: 'center', gap: 4 },
  dollar:       { fontSize: 32, fontWeight: 600, color: '#1D1D1F' },
  amountInput:  { fontSize: 32, fontWeight: 600, border: 'none', background: 'transparent', width: '100%', outline: 'none', color: '#1D1D1F' },
  input:        { width: '100%', padding: '13px 14px', border: '1.5px solid #F0F0F0', borderRadius: 12, fontSize: 14, background: '#FAFAFA', color: '#1D1D1F', outline: 'none', boxSizing: 'border-box' },
  saveBtn:      { width: '100%', padding: 16, background: '#007AFF', color: '#fff', border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 600, cursor: 'pointer' },

  empty:        { background: '#fff', borderRadius: 20, padding: '40px 20px', textAlign: 'center', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  emptyIcon:    { fontSize: 32, marginBottom: 12 },
  emptyText:    { fontSize: 15, fontWeight: 500, marginBottom: 4 },
  emptySub:     { fontSize: 13, color: '#6E6E73' },

  txList:       { background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  txItem:       { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid #F5F5F7', cursor: 'pointer' },
  txDot:        { width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 600, flexShrink: 0 },
  txInfo:       { flex: 1, minWidth: 0 },
  txLabel:      { fontSize: 14, fontWeight: 500, color: '#1D1D1F' },
  txDate:       { fontSize: 11, color: '#AEAEB2', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  txAmount:     { fontSize: 14, fontWeight: 600 },

  detail:       { borderTop: '1px solid #F5F5F7', padding: '12px 16px', background: '#FAFAFA' },
  detailRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontSize: 13, borderBottom: '1px solid #F0F0F0' },
  detailLabel:  { color: '#AEAEB2', flexShrink: 0 },
  detailValue:  { color: '#1D1D1F', fontWeight: 500, wordBreak: 'break-all', maxWidth: '60%', textAlign: 'right' },
  linkActions:  { display: 'flex', alignItems: 'center', gap: 8, maxWidth: '65%' },
  linkUrl:      { fontSize: 11, color: '#6E6E73', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  copyBtn:      { background: '#007AFF', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },

  actionRow:    { display: 'flex', gap: 8, marginTop: 12 },
  qrBtn:        { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 0', background: '#EBF4FF', color: '#007AFF', border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  deleteBtn:    { flex: 1, padding: '11px 0', background: '#FFF0EF', color: '#FF3B30', border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 600, cursor: 'pointer' },

  /* QR Modal */
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalCard:    { background: '#fff', borderRadius: 24, padding: '28px 24px 40px', width: '90%', maxWidth: 380, textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' },
  modalTitle:   { fontSize: 36, fontWeight: 700, letterSpacing: -1, marginBottom: 4 },
  modalDesc:    { fontSize: 14, color: '#6E6E73', marginBottom: 16 },
  qrWrap:       { display: 'flex', justifyContent: 'center', marginBottom: 12 },
  modalUrl:     { fontSize: 12, color: '#007AFF', marginBottom: 20, wordBreak: 'break-all' },
  modalBtns:    { display: 'flex', gap: 10, marginBottom: 12 },
  modalBtn:     { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '13px 0', background: '#F5F5F7', color: '#1D1D1F', border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  modalClose:   { width: '100%', padding: 16, background: '#1D1D1F', color: '#fff', border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 600, cursor: 'pointer' },
};
